/**
 * A top-down court outline in pickleball proportions (20 x 44), drawn
 * portrait with the net across the middle: sidelines and baselines, the net,
 * both kitchen (non-volley zone) lines and the two centre service lines.
 *
 * Purely decorative line art. Strokes use `currentColor`, so the caller sets
 * the colour with CSS `color`. Every line is a <path> with `pathLength="1"`
 * and a `--line` index, which lets a stylesheet "draw" the court in with
 * stroke-dashoffset — dash maths in units of 1 instead of each line's real
 * length — and stagger the lines off the index (see FirstVisitSplash.css).
 * Paths rather than <rect>/<line> because pathLength on basic shapes is not
 * honoured by every browser that guests use.
 *
 * Geometry is in a 220 x 460 viewBox: the court is 200 x 440 inset by 10,
 * net at y=230, kitchen lines 70 units (7ft) either side of it.
 *
 * @param {object} props
 * @param {string} [props.className] - sizing/animation class for the <svg>.
 * @param {number} [props.strokeWidth=4] - line weight in viewBox units.
 * @returns {JSX.Element}
 */
function CourtLines({ className = '', strokeWidth = 4 }) {
  // Drawing order: outline, net, kitchen lines, centre service lines.
  const lines = [
    { d: 'M10 10 H210 V450 H10 Z' },
    { d: 'M2 230 H218', modifier: 'court-net' },
    { d: 'M10 160 H210' },
    { d: 'M10 300 H210' },
    { d: 'M110 10 V160' },
    { d: 'M110 300 V450' },
  ];

  return (
    <svg
      className={className}
      viewBox="0 0 220 460"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {lines.map(({ d, modifier }, index) => (
        <path
          key={d}
          d={d}
          className={modifier ? `court-line ${modifier}` : 'court-line'}
          pathLength="1"
          style={{ '--line': index }}
        />
      ))}
    </svg>
  );
}

export default CourtLines;
